import { ReactNode } from 'react'
import { Box } from '@mui/material'

type KpiTileProps = {
  label: string
  /** Already formatted. `null`/`undefined` renders as an em dash, not "N/A". */
  value?: string | number | null
  /** A smaller trailing unit, set apart from the figure. */
  unit?: string
}

/**
 * One number, its label, and nothing else. The dashboard's summary strip is a
 * row of these. Shared with the Planner Portal's `Common/Components/KpiTile`.
 */
export const KpiTile = ({ label, value, unit }: KpiTileProps) => {
  const empty = value === null || value === undefined || value === ''

  return (
    <Box
      sx={{
        background: 'var(--surface-0)',
        px: '12px',
        py: '10px',
        minWidth: 0,
      }}
    >
      <Box
        sx={{
          fontSize: 10.5,
          fontWeight: 600,
          letterSpacing: '0.05em',
          textTransform: 'uppercase',
          color: 'var(--ink-500)',
          lineHeight: 1.2,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: '3px',
          mt: '6px',
          // Tabular figures are not optional: a KPI that re-renders must not
          // shuffle horizontally.
          fontFamily: 'var(--font-mono)',
          fontVariantNumeric: 'tabular-nums',
          fontSize: 22,
          fontWeight: 600,
          lineHeight: 1,
          color: empty ? 'var(--ink-300)' : 'var(--ink-900)',
        }}
      >
        {empty ? '—' : value}
        {!empty && unit && (
          <Box component="span" sx={{ fontSize: 12, color: 'var(--ink-500)' }}>
            {unit}
          </Box>
        )}
      </Box>
    </Box>
  )
}

/**
 * Lays tiles out with a 1px gap over a line-200 ground, which draws the
 * hairlines between them without a border on each tile.
 */
export const KpiStrip = ({ children }: { children: ReactNode }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
      gap: '1px',
      background: 'var(--line-200)',
      border: '1px solid var(--line-200)',
      borderRadius: 'var(--radius-md)',
      overflow: 'hidden',
    }}
  >
    {children}
  </Box>
)
